// Shared GA4 wrapper — identical across caudexology and ariumology.
// The gtag bootstrap in index.html is production-gated (only loads on the live
// host) and sets window.gaMeasurementId, so every helper below is a silent
// no-op in dev, preview, and bot/prerender contexts where gtag never loads.

type AnalyticsValue = string | number | boolean | undefined
export type AnalyticsParams = Record<string, AnalyticsValue>

declare global {
  interface Window {
    gaMeasurementId?: string
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

let previousPageLocation: string | undefined
let lastPageView: { location: string; trackedAt: number } | undefined

function compact(params: AnalyticsParams): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(params).filter((e): e is [string, string | number | boolean] => e[1] !== undefined),
  )
}

export function trackEvent(name: string, params: AnalyticsParams = {}): void {
  if (!window.gtag) return
  const id = window.gaMeasurementId
  window.gtag('event', name, compact(id ? { ...params, send_to: id } : params))
}

export function trackPageView(pagePath: string): void {
  if (!window.gtag) return

  const pageLocation = window.location.href
  const now = Date.now()

  // React StrictMode mounts effects twice in development. Ignore only the
  // immediate duplicate while still counting a genuine later revisit.
  if (lastPageView?.location === pageLocation && now - lastPageView.trackedAt < 250) return

  trackEvent('page_view', {
    page_title: document.title,
    page_location: pageLocation,
    page_path: pagePath,
    page_referrer: previousPageLocation || document.referrer || undefined,
  })

  lastPageView = { location: pageLocation, trackedAt: now }
  previousPageLocation = pageLocation
}
